import type { ApprovalChain as Chain } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * An approval chain, whole (F-090): every step, who is on it and what each
 * did, and - first, in words - who it is waiting on now. "'Pending approval'
 * without a named person is the most common cause of stalled deals."
 */
export function ApprovalChainView({ chain }: { chain: Chain }): JSX.Element {
  const t = useT();
  const format = useFormat();
  return (
    <div data-approval-chain={chain.requestId} className="flex flex-col gap-2">
      <p data-approval-waiting className="text-dense text-ink">
        {chain.status === 'pending'
          ? t('approvals.waitingOn', { values: { names: chain.waitingOn.join(', ') } })
          : t(`approvals.status.${chain.status}`)}
      </p>
      <ol className="flex flex-wrap items-start gap-2">
        {chain.steps.map((step) => (
          <li
            key={step.position}
            data-approval-step={step.position}
            data-state={step.state}
            className={
              step.state === 'current'
                ? 'flex min-w-0 flex-col gap-1 rounded-card border border-bronze p-2'
                : 'flex min-w-0 flex-col gap-1 rounded-card border border-rule p-2'
            }
          >
            <span className="text-dense text-ink">
              {String(step.position)}. {step.name}
              <span className="ms-1 text-meta text-ink-meta">({t(`approvals.mode.${step.mode}`)})</span>
            </span>
            <ul className="flex flex-col">
              {step.assignees.map((assignee) => (
                <li key={assignee.assignmentId} data-approver={assignee.name} data-approver-status={assignee.status} className="text-meta text-ink-600">
                  {assignee.name} · {t(`approvals.assignee.${assignee.status}`)}
                  {assignee.cameFromName ? ` · ${t('approvals.onBehalfOf', { values: { name: assignee.cameFromName } })}` : ''}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      <details className="text-meta text-ink-meta">
        <summary>{t('approvals.history')}</summary>
        <ul data-approval-history className="flex flex-col gap-1 pt-1">
          {chain.history.map((entry, index) => (
            <li key={`${entry.at}-${String(index)}`}>
              {formatDateTime(entry.at, format)} · {entry.actorName ?? t('approvals.system')} · {t(`approvals.action.${entry.action as "approved"}`)}
              {entry.targetName ? ` → ${entry.targetName}` : ''}
              {entry.via === 'email' ? ` · ${t('approvals.viaEmail')}` : ''}
              {entry.comment ? ` - "${entry.comment}"` : ''}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
